import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Alert, Box, Button, Grid, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from 'src/stores';
import { succeeded } from './asyncResult';
const EMPTY = {
  username: '',
  password: '',
  accountReference: '',
  serviceAddress: '',
  instructions: ''
};

/**
 * Capture / rotate a utility portal credential for one property utility.
 *
 * The password is WRITE-ONLY. It is never populated from a response, never rendered back, and there
 * is deliberately no reveal control - the authenticated API does not return the secret, and the UI
 * must not invent a way to ask for it. The field is cleared as soon as the save succeeds so the
 * secret does not sit in component state for the life of the page.
 */
export const CredentialForm = observer(_ref => {
  let companyId = _ref.companyId,
    utilityProviderId = _ref.utilityProviderId,
    propertyId = _ref.propertyId,
    propertyUtilityId = _ref.propertyUtilityId,
    utilityTypeId = _ref.utilityTypeId,
    propertyReference = _ref.propertyReference,
    existing = _ref.existing,
    onSaved = _ref.onSaved;
  const store = useStore();
  const _useState = useState(_objectSpread(_objectSpread({}, EMPTY), {}, {
      accountReference: (existing === null || existing === void 0 ? void 0 : existing.accountReference) || ''
    })),
    _useState2 = _slicedToArray(_useState, 2),
    form = _useState2[0],
    setForm = _useState2[1];
  const _useState3 = useState(''),
    _useState4 = _slicedToArray(_useState3, 2),
    error = _useState4[0],
    setError = _useState4[1];
  const _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    saving = _useState6[0],
    setSaving = _useState6[1];

  // Same reason as the section above: under saas-react-components v3 the modal keeps its children
  // mounted, so an account number that arrives after this mounts is no longer picked up by a
  // remount. Only fills an empty field, so it cannot overwrite what an operator is typing.
  useEffect(() => {
    setForm(current => current.accountReference ? current : _objectSpread(_objectSpread({}, current), {}, {
      accountReference: (existing === null || existing === void 0 ? void 0 : existing.accountReference) || ''
    }));
  }, [existing === null || existing === void 0 ? void 0 : existing.accountReference]);
  const createChangeHandler = field => event => {
    setForm(current => _objectSpread(_objectSpread({}, current), {}, {
      [field]: event.target.value
    }));
  };
  const handleSubmit = async () => {
    if (!form.username.trim() || !form.password) {
      setError('Portal username and password are both required.');
      return;
    }
    if (!companyId || !utilityProviderId) {
      setError('This utility is missing a company or provider, so a credential cannot be saved against it.');
      return;
    }
    setError('');
    setSaving(true);
    try {
      const resp = await store.admin.saveUtilityCredentialAsync.run({
        companyId,
        utilityProviderId,
        propertyId,
        propertyUtilityId,
        utilityTypeId,
        propertyReference,
        accountReference: form.accountReference.trim() || undefined,
        serviceAddress: form.serviceAddress.trim() || undefined,
        instructions: form.instructions.trim() || undefined,
        username: form.username.trim(),
        password: form.password
      });
      if (!succeeded(store.admin.saveUtilityCredentialAsync, resp)) {
        setError('The credential could not be saved. It was not stored, so nothing was changed.');
        return;
      }

      // Drop the secret from state the moment it is no longer needed.
      setForm(_objectSpread(_objectSpread({}, EMPTY), {}, {
        accountReference: form.accountReference
      }));
      if (onSaved) await onSaved();
    } catch (ex) {
      setError('The credential could not be saved. It was not stored, so nothing was changed.');
    } finally {
      setSaving(false);
    }
  };
  return /*#__PURE__*/React.createElement(Box, null, existing ? /*#__PURE__*/React.createElement(Alert, {
    severity: "info",
    sx: {
      marginBottom: '16px'
    }
  }, "A credential is already saved for this utility. Entering a new one replaces it; the old secret is overwritten and cannot be recovered.") : null, /*#__PURE__*/React.createElement(Typography, {
    variant: "body2",
    sx: {
      marginBottom: '16px'
    }
  }, "These credentials are encrypted before storage and are never displayed again. Only the masked username is shown after saving."), error ? /*#__PURE__*/React.createElement(Alert, {
    severity: "error",
    sx: {
      marginBottom: '16px'
    }
  }, error) : null, /*#__PURE__*/React.createElement(Grid, {
    container: true,
    spacing: 20
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    sm: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    label: "Portal username",
    value: form.username,
    onChange: createChangeHandler('username'),
    autoComplete: "off",
    inputProps: {
      'data-testid': 'portal-username'
    }
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    sm: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    type: "password",
    label: "Portal password",
    value: form.password,
    onChange: createChangeHandler('password')
    // new-password, not current-password: stops the browser offering to fill a Livable
    // login into a third-party portal field.
    ,
    autoComplete: "new-password",
    inputProps: {
      'data-testid': 'portal-password'
    }
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    sm: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    label: "Account number (optional)",
    value: form.accountReference,
    onChange: createChangeHandler('accountReference')
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12,
    sm: 6
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    label: "Service address (optional)",
    value: form.serviceAddress,
    onChange: createChangeHandler('serviceAddress')
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 12
  }, /*#__PURE__*/React.createElement(TextField, {
    fullWidth: true,
    multiline: true,
    minRows: 2,
    label: "Login instructions (optional)",
    value: form.instructions,
    onChange: createChangeHandler('instructions')
  }))), /*#__PURE__*/React.createElement(Box, {
    sx: {
      marginTop: '16px'
    }
  }, /*#__PURE__*/React.createElement(Button, {
    variant: "actionButton",
    disabled: saving,
    onClick: handleSubmit
  }, saving ? 'Saving...' : existing ? 'Replace credentials' : 'Save credentials')));
});